import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { IdealCandidateScore, RadarAxes, RadarScaleMarkers, RadarTitle } from './constants';

const MAX_SCORE = 100;

// Geometry of the hexagon, in svg units. The axis lines run a little past the
// outer ring, so the viewport keeps a margin around the ring.
const RADIUS = 110;
const CENTER = 132;
const VIEWPORT = CENTER * 2;
const AXIS_LENGTH = RADIUS * 1.12;
const AXIS_STEP = 360 / RadarAxes.length;
const RING_WIDTH = 1.5;
const AXIS_WIDTH = 1;
const SCALE_LABEL_OFFSET = 5;

// Distance from the centre to a flank vertex, and the gap a label keeps from
// the ring it belongs to.
const FLANK_X = Math.round(RADIUS * Math.cos(Math.PI / 6));
const FLANK_Y = Math.round(RADIUS / 2);
const LABEL_GAP = 16;

const PERFORMANCE_COLOR = '#002547';
const TARGET_COLOR = '#50B2B2';

const Legends = [
  { label: 'Your Performance', color: PERFORMANCE_COLOR },
  { label: 'Ideal Candidate', color: TARGET_COLOR }
];

// Where each label sits relative to the centre of the hexagon, in the order the
// axes are listed. Side labels are anchored by the edge facing the chart so the
// two on each flank line up with one another.
const LabelPlacements = [
  { textAlign: 'center', transform: `translate(-50%, calc(-100% - ${RADIUS + LABEL_GAP}px))` },
  { textAlign: 'left', transform: `translate(${FLANK_X + LABEL_GAP}px, calc(-50% - ${FLANK_Y}px))` },
  { textAlign: 'left', transform: `translate(${FLANK_X + LABEL_GAP}px, calc(-50% + ${FLANK_Y}px))` },
  { textAlign: 'center', transform: `translate(-50%, ${RADIUS + LABEL_GAP}px)` },
  { textAlign: 'right', transform: `translate(calc(-100% - ${FLANK_X + LABEL_GAP}px), calc(-50% + ${FLANK_Y}px))` },
  { textAlign: 'right', transform: `translate(calc(-100% - ${FLANK_X + LABEL_GAP}px), calc(-50% - ${FLANK_Y}px))` }
];

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  legendRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(4)
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flexShrink: 0
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  legendMarker: {
    width: '13px',
    height: '13px',
    borderRadius: '3px',
    flexShrink: 0
  },
  chart: {
    position: 'relative',
    width: '520px',
    height: '340px',
    margin: '0 auto'
  },
  shape: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)'
  },
  // Entrance animation, played once when the chart mounts. The page keeps a
  // skeleton up until the scores are curated, so the radar only ever mounts
  // with its real values and the grow reads as the chart arriving.
  //
  // `view-box` puts the transform origin at the centre of the viewBox, which is
  // the hexagon's centre, so both series grow out of the middle. The default
  // origin for an svg child is its own top-left, and `fill-box` would use each
  // polygon's own bounding box - the irregular performance shape would then
  // drift as it scaled.
  grid: {
    animation: '$fadeIn 0.4s ease-out both',
  },
  target: {
    transformBox: 'view-box',
    transformOrigin: 'center',
    animation: '$growIn 0.7s 0.15s cubic-bezier(0.22, 1, 0.36, 1) both',
  },
  performance: {
    transformBox: 'view-box',
    transformOrigin: 'center',
    animation: '$growIn 0.8s 0.35s cubic-bezier(0.22, 1, 0.36, 1) both',
  },
  label: {
    animation: '$fadeIn 0.5s 0.5s ease-out both',
  },
  '@keyframes fadeIn': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  '@keyframes growIn': {
    '0%': { opacity: 0, transform: 'scale(0)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
  // Respects a reduced-motion preference: everything is simply present.
  '@media (prefers-reduced-motion: reduce)': {
    grid: { animation: 'none' },
    target: { animation: 'none' },
    performance: { animation: 'none' },
    label: { animation: 'none' },
  },
}));

const clampScore = (score) => Math.min(Math.max(Number(score) || 0, 0), MAX_SCORE);

// kth vertex of the hexagon, clockwise from the top one.
const vertex = (index, radius) => {
  const angle = ((index * AXIS_STEP) - 90) * (Math.PI / 180);
  return [CENTER + (radius * Math.cos(angle)), CENTER + (radius * Math.sin(angle))];
};

const toPolygon = (scores) => scores
  .map((score, index) => vertex(index, (RADIUS * clampScore(score)) / MAX_SCORE)
    .map((coordinate) => coordinate.toFixed(2))
    .join(','))
  .join(' ');

const PerformanceRadar = ({ categories = [] }) => {
  const classes = useStyles();
  const theme = useTheme();

  // The ring carries a heavier stroke than the axis lines - on the page's
  // off-white background a hairline in the design's grey disappears.
  const gridColor = theme.palette.neutral['clr-200'];
  const scaleColor = theme.palette.neutral['clr-600'];
  const scaleFont = theme.typography['body02-medium'].fontFamily;

  const scores = React.useMemo(() => RadarAxes.map(({ categoryId, parameterId }) => {
    const category = categories.find(({ id }) => id === categoryId);
    const parameter = (category?.subCategories || []).find(({ id }) => id === parameterId);

    return clampScore(parameter?.score);
  }), [categories]);

  const target = React.useMemo(
    () => toPolygon(RadarAxes.map(() => IdealCandidateScore)),
    []
  );

  const performance = React.useMemo(() => toPolygon(scores), [scores]);

  return (
    <Box className={classes.container}>
      <Box className={classes.legendRow}>
        <Typography variant='h4-medium' color='neutral.clr-900'>
          {RadarTitle}
        </Typography>

        <Box className={classes.legend}>
          {Legends.map(({ label, color }) => (
            <Box key={label} className={classes.legendItem}>
              <Box className={classes.legendMarker} sx={{ backgroundColor: color }} />
              <Typography variant='body01-medium' color='neutral.clr-700'>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box className={classes.chart}>
        <svg
          className={classes.shape}
          width={VIEWPORT}
          height={VIEWPORT}
          viewBox={`0 0 ${VIEWPORT} ${VIEWPORT}`}
          aria-hidden='true'
        >
          {RadarScaleMarkers.map((marker) => (
            <polygon
              key={marker}
              className={classes.grid}
              points={toPolygon(RadarAxes.map(() => marker))}
              fill='none'
              stroke={gridColor}
              strokeWidth={marker === MAX_SCORE ? RING_WIDTH : AXIS_WIDTH}
            />
          ))}

          {RadarAxes.map(({ id }, index) => {
            const [x, y] = vertex(index, AXIS_LENGTH);

            return (
              <line
                key={id}
                className={classes.grid}
                x1={CENTER}
                y1={CENTER}
                x2={x.toFixed(2)}
                y2={y.toFixed(2)}
                stroke={gridColor}
                strokeWidth={AXIS_WIDTH}
              />
            );
          })}

          <polygon
            className={classes.target}
            points={target}
            fill={TARGET_COLOR}
            fillOpacity={0.15}
            stroke={TARGET_COLOR}
            strokeWidth={3}
            strokeLinejoin='round'
          />

          <polygon
            className={classes.performance}
            points={performance}
            fill={PERFORMANCE_COLOR}
            fillOpacity={0.25}
            stroke={PERFORMANCE_COLOR}
            strokeWidth={2.5}
            strokeLinejoin='round'
          />

          {/* Scale readings up the top axis, haloed so they stay legible
              wherever a series happens to cover them. */}
          {[0, ...RadarScaleMarkers].map((marker) => (
            <text
              key={marker}
              className={classes.label}
              x={CENTER + SCALE_LABEL_OFFSET}
              y={CENTER - ((RADIUS * marker) / MAX_SCORE) - 2}
              fill={scaleColor}
              stroke={theme.palette.shades['clr-white-900']}
              strokeWidth={3}
              paintOrder='stroke'
              fontFamily={scaleFont}
              fontSize={10}
            >
              {marker}
            </text>
          ))}
        </svg>

        {RadarAxes.map(({ id, label }, index) => (
          <Typography
            key={id}
            className={classes.label}
            variant='body01-bold'
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              maxWidth: '120px',
              fontSize: '15px',
              lineHeight: '22px',
              color: PERFORMANCE_COLOR,
              ...LabelPlacements[index]
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default PerformanceRadar;
